<!-- Modal de validación de PIN para refresh token -->
@if (showModal) {
  <div class="pin-modal-overlay" (click)="$event.stopPropagation()">
    <div class="pin-modal-container" (click)="$event.stopPropagation()">
      <div class="pin-modal-header">
        <div class="security-icon">
          <i class="fas fa-shield-alt"></i>
        </div>
        <h2>Verificación de Seguridad</h2>
        <p class="subtitle">Por tu seguridad, necesitamos verificar tu identidad</p>
      </div>
      <div class="pin-modal-body">
        <!-- Estado de carga inicial -->
        @if (isCheckingPasskey) {
          <div class="checking-passkey-state">
            <mat-spinner diameter="40"></mat-spinner>
            <p>Verificando métodos de autenticación...</p>
          </div>
        }
        <!-- Contenido principal (solo se muestra después de verificar) -->
        @if (!isCheckingPasskey) {
          <div>
            <!-- ============================================ -->
            <!-- VISTA DE PASSKEY (cuando activeMethod === 'passkey' y tiene passkey) -->
            <!-- ============================================ -->
            @if (showPasskeyOption && activeMethod === 'passkey') {
              <div class="passkey-view">
                <div class="passkey-section">
                  <div class="passkey-header">
                    <mat-icon class="passkey-icon">fingerprint</mat-icon>
                    <h3>Verificación Rápida</h3>
                    <p>Usa {{ biometricMethodName }} para continuar</p>
                  </div>
                  <button
                    type="button"
                    class="btn-passkey"
                    (click)="validateWithPasskey()"
                    [disabled]="isLoadingPasskey"
                    matTooltip="Usa tu {{ biometricMethodName }} para continuar de forma rápida y segura"
                    matTooltipPosition="above">
                    <mat-icon>fingerprint</mat-icon>
                    @if (!isLoadingPasskey) {
                      <span>
                        Verificar con {{ biometricMethodName }}
                      </span>
                    }
                    @if (isLoadingPasskey) {
                      <span>
                        Verificando...
                      </span>
                    }
                  </button>
                  @if (passkeyError) {
                    <div class="alert-error">
                      <mat-icon>error_outline</mat-icon>
                      <span>{{ passkeyError }}</span>
                    </div>
                  }
                </div>
                <!-- Botón para cambiar a PIN -->
                <div class="switch-method-section">
                  <p class="switch-text">Si prefieres, puedes solicitar un código de verificación por correo electrónico.</p>
                  <button type="button" class="btn-switch-method" (click)="switchToPinMethod()">
                    <mat-icon>email</mat-icon>
                    <span>Usar PIN por correo</span>
                  </button>
                </div>
              </div>
            }
            <!-- ============================================ -->
            <!-- VISTA DE PIN (cuando activeMethod === 'pin' o no tiene passkey) -->
            <!-- ============================================ -->
            @if (showPinForm) {
              <div class="pin-view">
                <!-- Mensaje informativo -->
                @if (pinWasSent) {
                  <div class="info-message">
                    <i class="fas fa-info-circle"></i>
                    <p>Hemos enviado un código de verificación a tu correo electrónico. Por favor, ingrésalo a continuación.</p>
                  </div>
                }
                <form [formGroup]="validateForm" (ngSubmit)="validate()">
                  <div class="form-group">
                    <label for="pin">Código de Verificación</label>
                    <div class="pin-input-wrapper">
                      <input
                        [type]="showPin ? 'text' : 'password'"
                        id="pin"
                        formControlName="pin"
                        class="form-control pin-input"
                        placeholder="Ingrese el código de 8 dígitos"
                        maxlength="8"
                        (keydown.enter)="onEnterKey($event)"
                        autocomplete="off"
                        autofocus
                        />
                        <button
                          type="button"
                          class="toggle-pin-btn"
                          (click)="toggleShowPin()"
                          title="{{ showPin ? 'Ocultar' : 'Mostrar' }} PIN"
                          >
                          <i [class]="showPin ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
                        </button>
                      </div>
                      @if (validateForm.get('pin')?.invalid && validateForm.get('pin')?.touched) {
                        <div class="validation-message">
                          @if (validateForm.get('pin')?.errors?.['required']) {
                            <small>
                              El código es requerido
                            </small>
                          }
                          @if (validateForm.get('pin')?.errors?.['minlength'] || validateForm.get('pin')?.errors?.['maxlength']) {
                            <small>
                              El código debe tener exactamente 8 dígitos
                            </small>
                          }
                        </div>
                      }
                    </div>
                    <div class="modal-actions">
                      <button
                        type="submit"
                        class="btn btn-primary btn-validate"
                        [disabled]="validateForm.invalid || isSubmittingPin || isResendingPin"
                        >
                        <i class="fas fa-check-circle"></i>
                        Validar Código
                      </button>
                      <button
                        type="button"
                        class="btn btn-secondary btn-resend"
                        (click)="resendPin()"
                        [disabled]="isResendingPin || isSubmittingPin"
                        >
                        <i class="fas fa-redo"></i>
                        Reenviar Código
                      </button>
                    </div>
                  </form>
                  <!-- Mensaje de ayuda dinámico -->
                  @if (helpMessage) {
                    <div class="help-section">
                      <p class="help-text" [class.error-text]="failedAttempts >= maxAttempts">
                        @if (failedAttempts >= maxAttempts) {
                          <i class="fas fa-exclamation-triangle"></i>
                        }
                        @if (failedAttempts < maxAttempts) {
                          <i class="fas fa-info-circle"></i>
                        }
                        {{ helpMessage }}
                      </p>
                    </div>
                  }
                  <!-- Información general -->
                  @if (!helpMessage) {
                    <div class="help-section">
                      <p class="help-text">
                        <i class="fas fa-question-circle"></i>
                        ¿No recibiste el código? Revisa tu carpeta de spam o solicita un nuevo código.
                      </p>
                    </div>
                  }
                  <!-- Contador de intentos -->
                  @if (failedAttempts > 0) {
                    <div class="attempts-counter">
                      <p class="attempts-text" [class.warning]="failedAttempts >= 2">
                        <i class="fas fa-exclamation-circle"></i>
                        Intentos fallidos: {{ failedAttempts }} / {{ maxAttempts }}
                      </p>
                    </div>
                  }
                  <div class="logout-section">
                    @if (failedAttempts >= maxAttempts) {
                      <p class="logout-help-text">
                        <i class="fas fa-info-circle"></i>
                        Si no puedes validar el PIN, puedes cerrar sesión e iniciar sesión nuevamente.
                      </p>
                    }
                    <button type="button" class="btn btn-logout" (click)="logout()" [class.highlighted]="failedAttempts >= maxAttempts">
                      <i class="fas fa-sign-out-alt"></i>
                      {{ failedAttempts >= maxAttempts ? 'Ir al Login' : 'Cerrar Sesión' }}
                    </button>
                  </div>
                  <!-- Botón para volver a Passkey (solo si tiene passkey y está en modo PIN) -->
                  @if (showPasskeyOption && activeMethod === 'pin') {
                    <div class="switch-method-section">
                      <button type="button" class="btn-switch-method-secondary" (click)="switchToPasskeyMethod()">
                        <mat-icon>fingerprint</mat-icon>
                        <span>Usar Passkey</span>
                      </button>
                    </div>
                  }
                </div>
              }
            </div>
            @if (passkeyAvailabilityState === 'error' && !pinExplicitlySelected) {
              <div class="passkey-error-state">
                <p>No pudimos consultar la passkey. Elige cómo continuar.</p>
                <button type="button" class="btn-switch-method" (click)="retryPasskeyLookup()">
                  <mat-icon>refresh</mat-icon>
                  <span>Reintentar</span>
                </button>
                <button type="button" class="btn-switch-method" (click)="switchToPinMethod()">
                  <mat-icon>email</mat-icon>
                  <span>Usar PIN por correo</span>
                </button>
              </div>
            }
          }
        </div>
        <div class="pin-modal-footer">
          <p class="security-note">
            <i class="fas fa-lock"></i>
            Esta verificación adicional protege tu cuenta de accesos no autorizados
          </p>
        </div>
      </div>
    </div>
  }
